import type { Platform } from '@app-iptv/shared'

import { getNativePlatformInfo } from './platform-native'

interface PlatformInfo {
  platform: Platform
  /** Versão da PLATAFORMA (webOS `sdkVersion`, Tizen `platform.version`, Android).
   *  `'0'` = desconhecida.
   *
   *  ⚠️ Em TV, a fonte é a **API nativa** (`platform-native.ts`), NÃO o UA: a LG
   *  não põe a versão do webOS na user-agent (UA real da TV do dono não tem token
   *  `Web0S/x.x`), e ler só do UA resolvia `'0'` pra toda TV LG — que o
   *  `platformTierCeiling` tratava como pior caso, classificando **qualquer LG
   *  como `vlow`**. O UA continua como fallback (algumas TVs o expõem). */
  version: string
  /** Modelo comercial. Em TV vem da API nativa (ex.: `'55SM8600PSA'`). */
  model: string | null
  /** Firmware do device (webOS: `'05.50.00'`). Diagnóstico/telemetria — NÃO é a
   *  versão do webOS (confundir os dois adianta a TV em duas gerações). */
  firmwareVersion: string | null
  ua: string
  /** Versão MAJOR do motor Chromium/Chrome extraída do UA (ex.: 38, 53, 120).
   *  null quando o UA não expõe `Chrome/`/`Chromium/` (Safari puro / WebKit
   *  antigo sem tag Chrome). É o proxy DETERMINÍSTICO de capacidade de
   *  compositing/GPU — o que trava TV velha (blur/shadow/transform) escala com
   *  a idade do MOTOR, não com o microbench de JS single-thread. Consumido em
   *  `platform-tier.ts` como TETO de tier. */
  chromiumVersion: number | null
}

declare global {
  interface Window {
    // NOTA: `Window.tizen`/`Window.webOS` também são declarados em
    // packages/i18n/src/detect-locale.ts (leitura de locale do sistema). Como
    // `declare global` MERGEIA por todo o programa, as duas declarações precisam
    // ter tipo IDÊNTICO nos campos compartilhados — por isso `getPropertyValue`
    // (usado só pelo i18n) e `service` (bridge Luna) aparecem aqui também.
    tizen?: {
      systeminfo?: {
        getCapability?: (key: string) => string
        getPropertyValue?: (
          property: string,
          onSuccess: (data: { language?: string; [k: string]: unknown }) => void,
          onError?: (err: unknown) => void,
        ) => void
      }
    }
    webOS?: {
      deviceInfo?: (cb: (d: { modelName?: string; sdkVersion?: string }) => void) => void
      service?: {
        request?: (
          uri: string,
          args: {
            method: string
            parameters?: Record<string, unknown>
            onSuccess?: (res: unknown) => void
            onFailure?: (err: unknown) => void
          },
        ) => unknown
      }
    }
  }
}

export function detectPlatformInfo(): PlatformInfo {
  if (typeof window === 'undefined') {
    return {
      platform: 'web',
      version: '0',
      model: null,
      firmwareVersion: null,
      ua: '',
      chromiumVersion: null,
    }
  }

  const ua = navigator.userAgent
  const uaLower = ua.toLowerCase()
  const chromiumVersion = parseChromiumVersion(ua)

  // Tizen (Samsung)
  if (uaLower.includes('tizen')) {
    // API nativa PRIMEIRO (`tizen.systeminfo` / `webapis.productinfo`); UA como
    // fallback. Ao contrário da LG, a Samsung costuma expor `Tizen 6.0` no UA —
    // então aqui o nativo é reforço, não a única fonte.
    const native = getNativePlatformInfo()
    const m = ua.match(/Tizen\s+([\d.]+)/i)
    return {
      platform: 'tizen',
      version: native.version ?? m?.[1] ?? '0',
      model: native.modelName,
      firmwareVersion: native.firmwareVersion,
      ua,
      chromiumVersion,
    }
  }

  // VIDAA (Hisense) — ANTES do webOS porque algumas VIDAA são baseadas em
  // webOS e teriam "Web0S" no UA; "VIDAA"/"Hisense" é mais específico e tem
  // prioridade. Detectar evita cair em 'web' e proxiar por engano (premissa:
  // TV vai direto). Hisense Android TV cai no branch androidtv (também direto).
  if (uaLower.includes('vidaa') || uaLower.includes('hisense')) {
    const m = ua.match(/VIDAA\/?([\d.]+)/i)
    return {
      platform: 'vidaa',
      version: m?.[1] ?? '0',
      model: null,
      firmwareVersion: null,
      ua,
      chromiumVersion,
    }
  }

  // webOS (LG)
  //
  // A versão vem da API NATIVA (`PalmSystem` + Luna `sdkVersion`), porque a LG
  // **não a põe no UA** — o UA real da TV do dono (LG 2019) é:
  //   `Mozilla/5.0 (Web0S; Linux/SmartTV) ... Chrome/53.0.2785.34 ... WebAppManager`
  // sem nenhum token `Web0S/x.x`. O regex abaixo fica como fallback (algumas TVs
  // o expõem), mas o caminho QUENTE é o nativo. Quando nenhum dos dois responde,
  // `'0'` sinaliza "desconhecida" e o `platform-tier` cai no `chromiumVersion`
  // (que é sinal suficiente) — NUNCA mais em "pior caso = vlow".
  if (uaLower.includes('web0s') || uaLower.includes('webos')) {
    const native = getNativePlatformInfo()
    const m = ua.match(/Web0?S\/([\d.]+)/i) ?? ua.match(/webOS.TV-(\d+)/i)
    return {
      platform: 'webos',
      version: native.version ?? m?.[1] ?? '0',
      model: native.modelName,
      firmwareVersion: native.firmwareVersion,
      ua,
      chromiumVersion,
    }
  }

  // Fire TV (Amazon)
  if (uaLower.includes('aft') || uaLower.includes('amazon') || uaLower.includes('silk')) {
    const m = ua.match(/AFT[A-Z0-9]+/)
    return {
      platform: 'firetv',
      version: extractAndroidVersion(ua),
      model: m?.[0] ?? null,
      firmwareVersion: null,
      ua,
      chromiumVersion,
    }
  }

  // Android TV
  if (uaLower.includes('android') && (uaLower.includes('tv') || uaLower.includes('bravia'))) {
    return {
      platform: 'androidtv',
      version: extractAndroidVersion(ua),
      // Resolve modelos conhecidos (Nvidia Shield, Mi Box) a partir do UA aqui,
      // no ponto que TEM o UA — o tier derivado (`platform-tier`/`capabilities`)
      // consome só o `model` normalizado, sem reparsear UA.
      model: detectAndroidTvModel(uaLower),
      firmwareVersion: null,
      ua,
      chromiumVersion,
    }
  }

  // Mobile (smartphone/tablet) — heurística simples
  if (/iphone|ipad|ipod|android|mobile/i.test(ua) && !uaLower.includes('tv')) {
    return {
      platform: 'mobile-pwa',
      version: extractAndroidVersion(ua),
      model: null,
      firmwareVersion: null,
      ua,
      chromiumVersion,
    }
  }

  return {
    platform: 'web',
    version: '0',
    model: null,
    firmwareVersion: null,
    ua,
    chromiumVersion,
  }
}

function extractAndroidVersion(ua: string): string {
  const m = ua.match(/Android\s+(\d+(?:\.\d+)?)/i)
  return m?.[1] ?? '0'
}

/** Extrai a versão MAJOR do motor Chromium do UA. Cobre `Chrome/NN` (padrão em
 *  Tizen/webOS/Android/Web modernos) e `Chromium/NN`. Retorna só o major (o que
 *  importa pra classe de capacidade). null quando ausente. */
function parseChromiumVersion(ua: string): number | null {
  const m = ua.match(/Chrom(?:e|ium)\/(\d+)/i)
  if (!m) return null
  const n = parseInt(m[1] ?? '', 10)
  return Number.isFinite(n) ? n : null
}

/** Normaliza o UA de Android TV pra uma CHAVE de modelo conhecido
 *  (`models.ts::KNOWN_MODELS`). Preserva a inteligência que vivia no
 *  `lookupKnownModel(model, ua)` legado, agora resolvida no ponto de detecção
 *  de plataforma (que é quem tem o UA). */
function detectAndroidTvModel(uaLower: string): string | null {
  if (uaLower.includes('shield')) return uaLower.includes('pro') ? 'shield-pro' : 'shield-tv'
  if (uaLower.includes('mibox4')) return 'mibox-4'
  if (uaLower.includes('mibox')) return 'mibox-3'
  return null
}
